import {MutableRefObject} from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {runAnalyticsQuery} from 'insights/analytics-queries/run-query/RunAnalyticsQueryActions';
import {getExtraDataToTrackRunQuery} from 'insights/analytics-queries/run-query/trackRunQuery';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {getRunQueryData} from 'insights/parameters/data/AnalyticsQueryOperations';
import {RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import omitKey from 'util/omitKey';
import {HookSetState} from 'util/Types';

import {AnalyticsParameterConfigGraphQL} from '../../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import areRunQueryDatasEqual from './areRunQueryDatasEqual';
import RunQueryDatasById from './RunQueryDatasById';

type AnalyticsQuery = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>['analyticsQueries'][number];

interface Actions {
  runAnalyticsQuery: typeof runAnalyticsQuery;
}

/**
 * Process block: run if it's not already fetched or there's new data to run it with, and update the hook's
 * state
 *
 *   - New data if pendingRunQueryData is different from lastRunQueryData
 *   - State to update: lastRunQueryDatasById, pendingRunQueryDatasById
 */
export default function processAnalyticsQuery({
  actions,
  analyticsDashboardViewStateId,
  analyticsQueryViewStateId,
  analyticsQuery,
  lastRunQueryDatasByIdRef,
  parameterConfigs,
  parameterValues,
  pendingRunQueryDatasById,
  runQueryState,
  setPendingRunQueryDatasById,
}: {
  actions: Actions;
  analyticsDashboardViewStateId: string;
  analyticsQuery: AnalyticsQuery;
  analyticsQueryViewStateId: string;
  lastRunQueryDatasByIdRef: MutableRefObject<RunQueryDatasById>;
  parameterConfigs: Array<AnalyticsParameterConfigGraphQL>;
  parameterValues: Array<AnalyticsParameterValue>;
  pendingRunQueryDatasById: RunQueryDatasById;
  runQueryState: RunQueryState;
  setPendingRunQueryDatasById: HookSetState<RunQueryDatasById>;
}): void {
  const alreadyFetched = runQueryState.status !== AsyncStatus.NOT_FETCHED;
  const pendingRunQueryData = pendingRunQueryDatasById[analyticsQuery.id];
  // Run the query if
  //   - it's not already fetched, or
  //   - the data the query should be run with (pendingRunQueryData) is different from the data the query was
  //     last run with (lastRunQueryData).
  const shouldRun = ((): boolean => {
    if (!alreadyFetched) {
      return true;
    }
    const lastRunQueryData = lastRunQueryDatasByIdRef.current[analyticsQuery.id];
    return (
      pendingRunQueryData != null &&
      (lastRunQueryData == null || !areRunQueryDatasEqual(pendingRunQueryData, lastRunQueryData))
    );
  })();

  if (shouldRun) {
    // Run the query, and update lastRunQueryData (since it stores the data the query was last run with)
    const runQueryData =
      pendingRunQueryData ??
      getRunQueryData({parameterConfigs, parameterValues, sqlQuery: analyticsQuery.sqlQuery});
    actions.runAnalyticsQuery(
      analyticsQuery,
      analyticsQueryViewStateId,
      runQueryData,
      runQueryState,
      getExtraDataToTrackRunQuery(analyticsDashboardViewStateId, alreadyFetched)
    );
    lastRunQueryDatasByIdRef.current[analyticsQuery.id] = runQueryData;
  }
  // Now that we've processed the block, clear pendingRunQueryData.
  //
  // Setting a new pendingRunQueryDatasById triggers a re-render, so we try to avoid it. Only clear the
  // pendingRunQueryData if it was there in the first place.
  if (pendingRunQueryData != null) {
    setPendingRunQueryDatasById(omitKey(pendingRunQueryDatasById, analyticsQuery.id));
  }
}
